import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon, AutoAwesome as MagicIcon } from '@mui/icons-material';
import { extractShipmentFromText, extractShipmentFromInvoice } from '../../services/aiService';

interface MagicToolModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmExtraction: (extractedData: any) => void;
}

export default function MagicToolModal({ open, onClose, onConfirmExtraction }: MagicToolModalProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [nlpText, setNlpText] = useState('');
  const [pastedQuery, setPastedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Extraction result preview screen
  const [previewData, setPreviewData] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPreviewData(null);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processSelectedFile(e.target.files[0]);
    }
  };

  const processSelectedFile = (file: File) => {
    setUploadedFile(file);
    setPreviewData(null);
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleExtract = async () => {
    setLoading(true);
    setProgress(20);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 15;
      });
    }, 200);

    try {
      let result: any = null;
      if (activeTab === 0 && uploadedFile) {
        // Invoice extraction simulation
        result = await extractShipmentFromInvoice(uploadedFile.name);
      } else if (activeTab === 1) {
        // Natural language text parsing
        result = extractShipmentFromText(nlpText);
      } else if (activeTab === 2) {
        // Pasted raw query parsing
        result = extractShipmentFromText(pastedQuery);
      }

      clearInterval(interval);
      setProgress(100);

      // Delay slightly for smooth transition
      setTimeout(() => {
        setPreviewData(result);
        setLoading(false);
      }, 400);
    } catch (err) {
      console.error(err);
      clearInterval(interval);
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (previewData) {
      // Pass a flag to parent that these fields were AI-filled
      onConfirmExtraction({
        ...previewData,
        aiFilledFields: [
          'pickup',
          'destination',
          'pickupPinCode',
          'destinationPinCode',
          'shipmentType',
          'weight',
          'boxes',
        ],
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setActiveTab(0);
    setNlpText('');
    setPastedQuery('');
    setUploadedFile(null);
    setPreviewData(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #EAEAEA',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MagicIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            AI Magic Tool Assist
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {!previewData ? (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 3, borderBottom: '1px solid #EAEAEA' }}
            >
              <Tab label="Upload Invoice" sx={{ textTransform: 'none' }} />
              <Tab label="Describe Shipment" sx={{ textTransform: 'none' }} />
              <Tab label="Paste Query" sx={{ textTransform: 'none' }} />
            </Tabs>

            {loading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  AI is extracting shipment details from your{' '}
                  {activeTab === 0 ? 'invoice' : activeTab === 1 ? 'description' : 'pasted text'}...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4, width: '80%', mx: 'auto' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {progress}% Complete
                </Typography>
              </Box>
            ) : (
              <Box>
                {activeTab === 0 && (
                  <Box
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleFileDrop}
                    onClick={triggerFileSelect}
                    sx={{
                      border: '2px dashed #B0B0B0',
                      borderRadius: '12px',
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                      backgroundColor: '#FAFAFA',
                      '&:hover': { borderColor: 'primary.main', backgroundColor: '#F4F8FD' },
                    }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept=".pdf,.png,.jpg,.jpeg"
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                    <Typography variant="subtitle1" fontWeight="500">
                      {uploadedFile ? uploadedFile.name : 'Drag & drop invoice document'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Supports PDF, PNG, JPG up to 10MB
                    </Typography>
                    {uploadedFile && (
                      <Button variant="outlined" size="small" sx={{ mt: 2, textTransform: 'none' }}>
                        Change Document
                      </Button>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    placeholder="Describe your shipment naturally. E.g., 'I want to send 3 boxes of electronics from Delhi to Mumbai. Each box is 10 kg and 40x30x20 cm.'"
                    value={nlpText}
                    onChange={e => setNlpText(e.target.value)}
                    sx={{ backgroundColor: '#FAFAFA' }}
                  />
                )}

                {activeTab === 2 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    placeholder="Paste shipment text, e.g. from customer emails or booking queries..."
                    value={pastedQuery}
                    onChange={e => setPastedQuery(e.target.value)}
                    sx={{ backgroundColor: '#FAFAFA' }}
                  />
                )}
              </Box>
            )}
          </>
        ) : (
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <MagicIcon color="success" sx={{ fontSize: 20 }} /> Extracted Shipment Details
            </Typography>

            <Card
              variant="outlined"
              sx={{ borderRadius: '12px', mb: 3, borderColor: 'success.light', backgroundColor: '#F6FCF8' }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Origin City / Pincode
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {previewData.pickup?.city || 'Not found'} ({previewData.pickupPinCode || 'N/A'})
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Destination City / Pincode
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {previewData.destination?.city || 'Not found'} ({previewData.destinationPinCode || 'N/A'})
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Shipment Type
                    </Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ textTransform: 'capitalize' }}>
                      {previewData.shipmentType || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Declared Weight
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {previewData.weight || 'N/A'} {previewData.weightUnit}
                    </Typography>
                  </Grid>

                  {previewData.boxes && previewData.boxes.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Package List ({previewData.boxes.length} Box(es))
                      </Typography>
                      {previewData.boxes.map((box: any, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 1,
                            backgroundColor: '#FFF',
                            border: '1px solid #EAEAEA',
                            borderRadius: '6px',
                            mb: 1,
                            display: 'flex',
                            justifyItems: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: '500' }}>
                            Box {idx + 1}: {box.weight} kg - {box.length}x{box.width}x{box.height} cm
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                            {box.description || 'General cargo'}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="caption" color="text.secondary" display="block">
              Please review the extracted information. Confirmed data will populate the form where you can edit it
              before checking rates.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px solid #EAEAEA', display: 'flex', gap: 1 }}>
        {!previewData ? (
          <>
            <Button onClick={handleClose} variant="outlined" sx={{ textTransform: 'none', borderRadius: '20px' }}>
              Cancel
            </Button>
            <Button
              onClick={handleExtract}
              variant="contained"
              disabled={
                loading ||
                (activeTab === 0 && !uploadedFile) ||
                (activeTab === 1 && !nlpText.trim()) ||
                (activeTab === 2 && !pastedQuery.trim())
              }
              sx={{ textTransform: 'none', borderRadius: '20px', display: 'flex', gap: 0.5 }}
            >
              <MagicIcon sx={{ fontSize: 16 }} /> Extract Details
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => setPreviewData(null)}
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '20px' }}
            >
              Back
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="success"
              sx={{ textTransform: 'none', borderRadius: '20px' }}
            >
              Populate & Review Form
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
